import {
  HHAlert,
  HHButton,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { InferType } from 'yup';
import { useDynamicDialogContext } from '../../context/dynamic-dialog.context';
import { useSnackbarContext } from '../../context/snackbar.context';
import { useRunClaimsManualWebDagMutation } from '../../types';
import LoadingComponent from '../utils/loading';
import BillToolForm from './billing-form';
import ToolRunStatus, { DagRunStatus, RunStatusType } from './tool-run-status';
import {
  EligibilitySearchDepth,
  ToolRunMode,
  ToolValidationSchema,
} from './tool-validation-schema';
import { useBillingToolData } from './use-billing-tool-data';
import { useToolUrlPrefill } from './use-tool-url-prefill';

const BillToolRun = (): JSX.Element => {
  const { setDialogContent } = useDynamicDialogContext();
  const { setSnackbarState } = useSnackbarContext();
  const [runClaimsManualWebDagMutation] = useRunClaimsManualWebDagMutation();

  const methods = useForm({
    mode: 'onChange',
    resolver: yupResolver(ToolValidationSchema),
    criteriaMode: 'all',
    defaultValues: {
      runMode: ToolRunMode.PREVIEW,
      eligibilitySearchDepth: EligibilitySearchDepth.STANDARD,
      claimsLimit: 0,
      paymentTypes: null,
      excludePartnership: false,
      runAdminFeeClaims: false,
      startDate: dayjs()
        .subtract(1, 'month')
        .startOf('month')
        .subtract(2, 'month')
        .toDate(),
      endDate: dayjs().subtract(1, 'month').endOf('month').toDate(),
    },
  });

  const { handleSubmit, formState, reset, watch, resetField, setValue } =
    methods;
  const { isValid, isSubmitting, isDirty } = formState;

  const runMode = watch('runMode');
  const client = watch('client');
  const insurer = watch('insurer');
  const partnership = watch('partnership');
  const startDate = watch('startDate');
  const endDate = watch('endDate');

  const {
    clientOptions,
    insurerOptions,
    partnershipOptions,
    billingTypeOptions,
    paymentTypeOptions,
    isFilterLoading,
    isInitialLoading,
    hasError,
    hasData,
  } = useBillingToolData({
    clientId: client?.id,
    insurerId: insurer?.id,
    partnershipId: partnership?.id,
    startDate,
    endDate,
  });

  useToolUrlPrefill({
    clientOptions,
    insurerOptions,
    partnershipOptions,
    setValue,
  });

  const onFormSubmit: SubmitHandler<
    InferType<typeof ToolValidationSchema>
  > = formData => {
    const claimsClient = formData.client?.identifier.toLowerCase() || '';
    const finalData = {
      claimsStartDate: dayjs(formData.startDate).format('YYYY-MM-DD'),
      claimsEndDate: dayjs(formData.endDate).format('YYYY-MM-DD'),
      claimsClient,
      clientId: claimsClient === 'all' ? null : (formData.client?.id ?? null),
      paymentTypes: formData.paymentTypes,
      claimsLimit: formData.claimsLimit,
      insurerId: formData.insurer?.id ?? null,
      partnership: formData.partnership?.name ?? null,
      partnershipId: formData.partnership?.id ?? null,
      eligibilitySearchDepth: formData.eligibilitySearchDepth,
      programIndication: null,
      excludePartnerships: formData.excludePartnership,
      runAdminFeeClaims: formData.runAdminFeeClaims,
      mode: formData.runMode,
      claimsBillingType: formData.billingType.toLowerCase(),
    };

    return runClaimsManualWebDagMutation({
      variables: {
        dagRunRequestInput: {
          conf: finalData,
        },
      },
    })
      .then(({ data }) => {
        if (!data?.runClaimsManualWebDag) {
          throw new Error('Failed to run billing tool');
        }
        setSnackbarState('Billing tool run successfully', 'success', 4000);
        const runStatus: RunStatusType[] = JSON.parse(
          localStorage.getItem('runStatus') ?? '[]',
        );
        runStatus.unshift({
          dagId: data.runClaimsManualWebDag.dagId,
          dagRunId: data.runClaimsManualWebDag.dagRunId,
          note: data.runClaimsManualWebDag.note,
          state: data.runClaimsManualWebDag.state as DagRunStatus,
          logicalDate: dayjs(data.runClaimsManualWebDag.logicalDate).format(
            'L LT',
          ),
          conf: finalData,
        });
        if (runStatus.length > 10) {
          runStatus.length = 10;
        }
        localStorage.setItem('runStatus', JSON.stringify(runStatus));
      })
      .catch(() => {
        setSnackbarState('Billing tool run failed', 'error', 4000);
      })
      .finally(() => {
        resetField('runMode', { defaultValue: ToolRunMode.PREVIEW });
      });
  };

  if (isInitialLoading) {
    return <LoadingComponent center />;
  }

  if (hasError) {
    return (
      <HHAlert hhVariant="outlined" severity="error">
        <HHTypography hhVariant="body">
          There was an error loading client insurer list
        </HHTypography>
      </HHAlert>
    );
  }

  if (!hasData) {
    return (
      <HHStack spacing={2} overflow="hidden">
        <HHTypography hhVariant="body">Client info not found</HHTypography>
      </HHStack>
    );
  }

  return (
    <FormProvider {...methods}>
      <HHStack spacing={8} direction="column" padding={6}>
        <HHTypography hhVariant="section-title">
          Bill Submission Tool
        </HHTypography>
        <BillToolForm
          clientOptions={clientOptions}
          insurerOptions={insurerOptions}
          partnershipOptions={partnershipOptions}
          paymentTypeOptions={paymentTypeOptions}
          billingTypeOptions={billingTypeOptions}
          isFilterLoading={isFilterLoading}
        />
        <HHStack direction="row" spacing={4} alignSelf="end">
          <HHButton
            hhVariant="variant-bypass"
            variant="outlined"
            size="medium"
            color="primary"
            disabled={isSubmitting}
            onClick={(): void => {
              setDialogContent({
                title: (
                  <HHTypography hhVariant="section-title">
                    Run Status
                  </HHTypography>
                ),
                content: <ToolRunStatus />,
                fullWidth: true,
                width: 'md',
                footer: (
                  <HHTypography hhVariant="muted">
                    <i>* These runs are saved in your browser local storage</i>
                  </HHTypography>
                ),
              });
            }}
          >
            Run Status
          </HHButton>
          <HHButton
            hhVariant="variant-bypass"
            variant="outlined"
            size="medium"
            color="primary"
            onClick={(): void => {
              reset();
            }}
          >
            Clear
          </HHButton>
          <HHButton
            hhVariant="variant-bypass"
            variant="contained"
            size="medium"
            color={runMode === ToolRunMode.PREVIEW ? 'primary' : 'error'}
            disabled={!isDirty || !isValid || isSubmitting}
            onClick={handleSubmit(onFormSubmit)}
          >
            {runMode === ToolRunMode.PREVIEW ? 'Run Preview' : 'Run Submission'}
          </HHButton>
        </HHStack>
      </HHStack>
    </FormProvider>
  );
};

export default BillToolRun;
